import {ActionIcon, Divider, Group, Menu, Title} from "@mantine/core";
import {ObjectMappingDataTable} from "../../data-table/ObjectMappingDataTable/ObjectMappingDataTable";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {
  Entity,
  getEntityIdentifier,
  objectComboMappingsSelector,
  objectMappingsSelector,
  useGetObjectComboMappingsByObjectQuery,
  useGetObjectMappingsByObjectQuery
} from "@ots-frontend/ots-data";
import {Card, CardProps} from "../Card/Card";
import {useNavigate} from "react-router-dom";
import {IconPlus} from "@tabler/icons-react";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {ObjectComboMappingDataTable} from "../../data-table/ObjectComboMappingDataTable/ObjectComboMappingDataTable";

export type ObjectMappingDataTableCardProps = CardProps & {
  entity: Entity
}

export const ObjectMappingDataTableCard = (props: ObjectMappingDataTableCardProps) => {
  const {
    entity,
    shadow = "sm",
    padding = "lg",
    radius = "md",
    withBorder = true,
    ...rest
  } = props

  const {t} = useTranslation(["common"])
  const navigate = useNavigate()

  const selectObjectMappings = useMemo(() => {
    return objectMappingsSelector();
  }, []);

  const {objectMappings, isObjectMappingsLoading} = useGetObjectMappingsByObjectQuery({
    id: entity.id,
    type: entity.entityType,
  }, {
    selectFromResult: (result) => ({
      objectMappings: selectObjectMappings(result),
      isObjectMappingsLoading: result.isLoading
    })
  });

  const selectObjectComboMappings = useMemo(() => {
    return objectComboMappingsSelector();
  }, []);

  const {objectComboMappings, isObjectComboMappingsLoading} = useGetObjectComboMappingsByObjectQuery({
    id: entity.id,
    type: entity.entityType,
  }, {
    selectFromResult: (result) => ({
      objectComboMappings: selectObjectComboMappings(result),
      isObjectComboMappingsLoading: result.isLoading
    })
  });

  const onNewMappingClick = useCallback(() => {
    navigate("/object-mappings/form", {
      state: {
        id: undefined,
        formDefaults: {
          object: getEntityIdentifier(entity)
        }
      }
    });
  }, [navigate, entity])

  const onNewComboMappingClick = useCallback(() => {
    navigate("/object-combo-mappings/form", {
      state: {
        id: undefined,
        formDefaults: {
          leftObject: getEntityIdentifier(entity)
        }
      }
    });
  }, [navigate, entity])

  return (
    <Card shadow={shadow} padding={padding} radius={radius} withBorder={withBorder} {...rest}>
      <Group position="apart" pb="sm">
        <Title order={4}>{t("common.entity.objectmapping.plural", "Object Mappings")}</Title>
        <Group spacing={4} position="left" noWrap>
          <HasAccess permissions={["objectmapping_edit"]}>
            <Menu
              width={260}
              position="bottom-end"
              transitionProps={{transition: "pop-top-right"}}
              withinPortal
            >
              <Menu.Target>
                <ActionIcon color="green">
                  <IconPlus size={16}/>
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={onNewMappingClick}>
                  {t("common.actions.new", "New") + " " + t("common.entity.objectmapping", "Object Mapping")}
                </Menu.Item>
                <Menu.Item onClick={onNewComboMappingClick}>
                  {t("common.actions.new", "New") + " " + t("common.entity.objectcombomapping", "Object Combo Mapping")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </HasAccess>
        </Group>
      </Group>

      <Divider pb="sm"/>

      <Title order={6} pb="md">
        Mappings
      </Title>

      <ObjectMappingDataTable
        withBorder
        records={objectMappings}
        fetching={isObjectMappingsLoading}
      />

      <Divider my="lg"/>

      <Title order={6} pb="md">
        Combination Mappings
      </Title>

      <ObjectComboMappingDataTable
        withBorder
        records={objectComboMappings}
        fetching={isObjectComboMappingsLoading}
      />
    </Card>
  )
}
